import React, { useState } from 'react';
import { UserCheck, Award, Grid3x3, Tag, Shield, Clock, Headphones, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { whyChooseUs, faqData } from '../data/mock';
import SEO from '../components/SEO';
import { seoMetadata, structuredData } from '../data/seo';

const WhyChooseUs = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const additionalFeatures = [
    {
      icon: Shield,
      title: 'Genuine Products',
      description: 'All products are sourced directly from authorized dealers with proper warranties'
    },
    {
      icon: Clock,
      title: 'Time-Saving',
      description: 'Everything under one roof - no need to visit multiple stores'
    },
    {
      icon: Headphones,
      title: 'After-Sales Support',
      description: 'Continued assistance even after purchase for your peace of mind'
    },
    {
      icon: TrendingUp,
      title: 'Latest Trends',
      description: 'Stay updated with modern designs and innovative material solutions'
    }
  ];

  return (
    <div className="min-h-screen">
      <SEO 
        title={seoMetadata.whyChooseUs.title}
        description={seoMetadata.whyChooseUs.description}
        keywords={seoMetadata.whyChooseUs.keywords}
        canonical={seoMetadata.whyChooseUs.canonical}
        additionalStructuredData={structuredData.faq}
      />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1758448500596-ce0e0239f1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBob21lJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBsaXZpbmclMjByb29tJTIwcHJlbWl1bXxlbnwwfHx8fDE3NzExNDg3NDF8MA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        
        <div className="relative z-10 container mx-auto px-4 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Why Choose Us?</h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto text-gray-200">
            Experience the Difference with Lucknow's Most Trusted Showroom
          </p>
        </div>
      </section>

      {/* Main Features with Images */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              What Makes Us Stand Out
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Four pillars of excellence that define our service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {whyChooseUs.map((feature, index) => {
              const IconComponent = 
                feature.icon === 'UserCheck' ? UserCheck :
                feature.icon === 'Award' ? Award :
                feature.icon === 'Grid3x3' ? Grid3x3 : Tag;

              return (
                <Card key={feature.id} className="overflow-hidden border-0 shadow-xl hover:shadow-2xl transition-shadow bg-white group">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={feature.image}
                      alt={feature.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-red-600 rounded-xl flex items-center justify-center shadow-lg">
                        <IconComponent className="w-7 h-7 text-white" />
                      </div>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Benefits */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              More Reasons to Choose Us
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Going beyond expectations to serve you better
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {additionalFeatures.map((feature, index) => {
              const IconComponent = feature.icon;
              return (
                <Card key={index} className="text-center border-0 shadow-lg hover:shadow-xl transition-shadow bg-white p-6">
                  <CardContent className="p-0">
                    <div className="w-16 h-16 bg-gradient-to-br from-red-600 to-red-700 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-lg">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                    <p className="text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Bombay Home Gallery vs Others
              </h2>
              <p className="text-xl text-gray-600">
                See the difference for yourself
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Us */}
              <Card className="border-2 border-red-600 shadow-xl bg-gradient-to-br from-red-50 to-white">
                <CardContent className="p-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="px-4 py-2 bg-red-600 text-white rounded-full font-bold">
                      Bombay Home Gallery
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-800">Civil engineer with construction expertise</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-800">Only branded & certified products</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-800">Transparent, competitive pricing</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-800">Complete product range under one roof</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✓</span>
                      </div>
                      <span className="text-gray-800">Technical guidance & after-sales support</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* Others */}
              <Card className="border-2 border-gray-300 shadow-lg bg-white">
                <CardContent className="p-8">
                  <div className="flex items-center justify-center mb-6">
                    <div className="px-4 py-2 bg-gray-300 text-gray-700 rounded-full font-bold">
                      Other Stores
                    </div>
                  </div>
                  <ul className="space-y-4">
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✗</span>
                      </div>
                      <span className="text-gray-600">Limited technical knowledge</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✗</span>
                      </div>
                      <span className="text-gray-600">Mix of branded & local products</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✗</span>
                      </div>
                      <span className="text-gray-600">Often inflated prices</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✗</span>
                      </div>
                      <span className="text-gray-600">Need to visit multiple stores</span>
                    </li>
                    <li className="flex items-start space-x-3">
                      <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                        <span className="text-white text-sm">✗</span>
                      </div>
                      <span className="text-gray-600">Limited post-purchase assistance</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-xl text-gray-600">
                Got questions? We've got answers
              </p>
            </div>

            <div className="space-y-4">
              {faqData.map((faq, index) => (
                <Card 
                  key={index} 
                  className={`border-0 shadow-lg cursor-pointer transition-all duration-300 ${openFaq === index ? 'ring-2 ring-red-600' : 'hover:shadow-xl'}`}
                  onClick={() => toggleFaq(index)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                      {openFaq === index ? (
                        <ChevronUp className="w-5 h-5 text-red-600 flex-shrink-0" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      )}
                    </div>
                    {openFaq === index && (
                      <p className="mt-4 text-gray-600 leading-relaxed border-t pt-4">
                        {faq.answer}
                      </p>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">10+</div>
              <div className="text-gray-600 font-medium">Years in Business</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">5000+</div>
              <div className="text-gray-600 font-medium">Happy Customers</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">500+</div>
              <div className="text-gray-600 font-medium">Products Available</div>
            </div>
            <div className="text-center">
              <div className="text-4xl md:text-5xl font-bold text-red-600 mb-2">100%</div>
              <div className="text-gray-600 font-medium">Customer Satisfaction</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhyChooseUs;
